import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type NodeChange,
  type NodeTypes,
  type EdgeTypes,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { Topology } from '@/services/observability-api';
import { cn } from '@/lib/utils';
import { useObservabilityGraphStore } from '@/stores/observability-graph.store';
import { ArchitectureGraphEdge } from './ArchitectureGraphEdge';
import { nodePresentation } from './node-presentation';
import { ArchitectureGraphNode } from './ArchitectureGraphNode';
import { ArchitectureNodeContextMenu } from './ArchitectureNodeContextMenu';
import {
  createFlowTopology,
  detailLevelForZoom,
  edgeMarkerColor,
  layoutFlowTopology,
} from './graph-utils';
import type {
  ArchitectureFlowEdge,
  ArchitectureFlowNode,
  MetricOverlay,
  ProtocolFilter,
  SelectedTopologyItem,
} from './types';

const nodeTypes = { architecture: ArchitectureGraphNode } as unknown as NodeTypes;
const edgeTypes = { architecture: ArchitectureGraphEdge } as unknown as EdgeTypes;

export interface ArchitectureGraphProps {
  topology: Topology;
  protocol: ProtocolFilter;
  overlay: MetricOverlay;
  selected: SelectedTopologyItem;
  zoomOnClick: boolean;
  onSelect: (selection: Exclude<SelectedTopologyItem, null>) => void;
  visibleNodeIds?: string[];
  onShowAdjacent: (nodeId: string) => void;
  onHideAdjacent: (nodeId: string) => void;
  onHideNode: (nodeId: string) => void;
  controlActions?: ReactNode;
  className?: string;
}

export function ArchitectureGraph({
  topology,
  protocol,
  overlay,
  selected,
  zoomOnClick,
  onSelect,
  visibleNodeIds,
  onShowAdjacent,
  onHideAdjacent,
  onHideNode,
  controlActions,
  className,
}: ArchitectureGraphProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<ArchitectureFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<ArchitectureFlowEdge>([]);
  const [zoom, setZoom] = useState(1);
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance<
    ArchitectureFlowNode,
    ArchitectureFlowEdge
  > | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    nodeId: string;
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const positions = useObservabilityGraphStore((state) => state.positions);
  const setPositions = useObservabilityGraphStore((state) => state.setPositions);
  const graph = useMemo(
    () =>
      createFlowTopology(
        topology,
        protocol,
        overlay,
        detailLevelForZoom(zoom),
        visibleNodeIds ? new Set(visibleNodeIds) : undefined
      ),
    [overlay, protocol, topology, visibleNodeIds, zoom]
  );

  useEffect(() => {
    let active = true;
    void layoutFlowTopology(graph.nodes, graph.edges, positions).then((layoutNodes) => {
      if (!active) return;
      setNodes(
        layoutNodes.map((node) => ({
          ...node,
          selected: selected?.type === 'node' && selected.id === node.id,
        }))
      );
      setEdges(
        graph.edges.map((edge) => ({
          ...edge,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: edgeMarkerColor(edge.data!),
            width: 24,
            height: 24,
          },
          selected: selected?.type === 'edge' && selected.id === edge.id,
        }))
      );
      setPositions(Object.fromEntries(layoutNodes.map((node) => [node.id, node.position])));
    });
    return () => {
      active = false;
    };
  }, [graph, positions, selected, setEdges, setNodes, setPositions]);

  const resolvedFocusedNodeIds = useMemo(() => {
    if (!selected) return [];
    if (selected.type === 'node') return [selected.id];
    const edge = topology.edges.find((item) => item.id === selected.id);
    return edge ? [...new Set([edge.source, edge.target])] : [];
  }, [selected?.id, selected?.type, topology.edges]);
  const focusedNodeIdsKey = JSON.stringify(resolvedFocusedNodeIds);
  const focusedNodeIds = useMemo(
    () => JSON.parse(focusedNodeIdsKey) as string[],
    [focusedNodeIdsKey]
  );

  useEffect(() => {
    if (!flowInstance || !zoomOnClick || focusedNodeIds.length === 0) return;
    flowInstance.fitView({
      nodes: focusedNodeIds.map((id) => ({ id })),
      duration: 250,
      padding: 0.7,
    });
  }, [flowInstance, focusedNodeIds, zoomOnClick]);

  const handleNodesChange = useCallback(
    (changes: NodeChange<ArchitectureFlowNode>[]) => {
      onNodesChange(changes);
      const moved = Object.fromEntries(
        changes.flatMap((change) => {
          if (change.type !== 'position' || !change.position) return [];
          return [[change.id, change.position]];
        })
      );
      if (Object.keys(moved).length > 0) setPositions(moved);
    },
    [onNodesChange, setPositions]
  );

  return (
    <div
      ref={graphContainerRef}
      className={cn('relative h-[620px] min-w-0 flex-1 bg-neutral-50', className)}
      aria-label="Service dependency graph"
    >
      <ReactFlow<ArchitectureFlowNode, ArchitectureFlowEdge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_, node) => onSelect({ type: 'node', id: node.id })}
        onNodeContextMenu={(event, node) => {
          event.preventDefault();
          const bounds = graphContainerRef.current?.getBoundingClientRect();
          const menuWidth = 240;
          const menuHeight = 152;
          const relativeX = event.clientX - (bounds?.left ?? 0);
          const relativeY = event.clientY - (bounds?.top ?? 0);
          setContextMenu({
            nodeId: node.id,
            label: node.data.topologyNode.label,
            x: Math.max(8, Math.min(relativeX, (bounds?.width ?? menuWidth) - menuWidth)),
            y: Math.max(8, Math.min(relativeY, (bounds?.height ?? menuHeight) - menuHeight)),
          });
          onSelect({ type: 'node', id: node.id });
        }}
        onEdgeClick={(_, edge) => onSelect({ type: 'edge', id: edge.id })}
        onMove={(_, viewport) => setZoom(viewport.zoom)}
        onInit={setFlowInstance}
        fitView
        minZoom={0.35}
        maxZoom={2.5}
        panOnScroll
        zoomOnScroll={false}
        zoomOnPinch
        nodesDraggable
        nodesFocusable
        edgesFocusable
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} />
        <Controls showInteractive={false}>{controlActions}</Controls>
        <MiniMap<ArchitectureFlowNode>
          pannable
          zoomable
          nodeColor={(node) => nodePresentation(node.data.topologyNode).minimapColor}
          ariaLabel="Architecture graph minimap"
        />
      </ReactFlow>
      {contextMenu && (
        <ArchitectureNodeContextMenu
          label={contextMenu.label}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onShowAdjacent={() => {
            onShowAdjacent(contextMenu.nodeId);
            setContextMenu(null);
          }}
          onHideAdjacent={() => {
            onHideAdjacent(contextMenu.nodeId);
            setContextMenu(null);
          }}
          onHideNode={() => {
            onHideNode(contextMenu.nodeId);
            setContextMenu(null);
          }}
        />
      )}
    </div>
  );
}
